import React, { useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useBoardStore } from '../store/useBoardStore';

/**
 * Read-only viewer for conversation pins shared on the canvas.
 * Uses board.sharedPinnedConversations — not private per-user chats.
 */
export default function PinnedChatViewer({
  open,
  conversationId,
  onClose,
}) {
  const board = useBoardStore(s => s.board);

  const conversation = useMemo(() => {
    if (!conversationId) return null;
    const list = board?.sharedPinnedConversations || [];
    return list.find(c => String(c.id) === String(conversationId)) || null;
  }, [board?.sharedPinnedConversations, conversationId]);

  const messages = conversation?.messages || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Typography component="span" variant="h6" sx={{ flex: 1 }}>
          {conversation?.title || 'Pinned chat'}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!conversation ? (
          <Typography color="text.secondary">
            This pinned chat is not available to viewers.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {messages.map((msg, index) => (
              <Box key={`${msg.at || index}-${msg.role}`}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
                >
                  {msg.role === 'assistant' ? 'Assistant' : 'User'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
                {index < messages.length - 1 ? <Divider sx={{ mt: 1.5 }} /> : null}
              </Box>
            ))}
            {!messages.length ? (
              <Typography color="text.secondary">No messages in this pin.</Typography>
            ) : null}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
